import React from 'react';
import { Container, Button } from 'react-bootstrap';
import { LayoutGrid, LayoutList } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ExerciseList = ({ exercises, selectedExercise, onSelectExercise, completedExercises, layoutMode = "horizontal", onToggleLayout }) => {
  const { t } = useTranslation();

  const getCardStyles = (isCompleted, idStoragePreview) => {

    const baseStyles = {
      color: 'white',
      borderRadius: '16px',
      width: '16rem',
      height: '10rem',
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.3s ease, transform 0.3s ease',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: 'none',
      overflow: 'hidden'
    };

    if (idStoragePreview && idStoragePreview.trim() !== '') {
      return {
        ...baseStyles,
        backgroundImage: `url("${idStoragePreview}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#22bcc2' // Fallback color
      };
    } else {
      return {
        ...baseStyles,
        backgroundColor: isCompleted ? '#67e8f9' : '#22bcc2'
      };
    }
  };

  // Renderizar en modo horizontal (actual)
  if (layoutMode === "horizontal") {
    return (
      <>
        {/* Título del ejercicio actual - FUERA del panel */}
        <Container className="text-center py-2">
          <h3 className="text-white fw-normal mb-1" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>
            {selectedExercise?.name || t('move.exerciseLabel')}
          </h3>
          {selectedExercise?.routineName && (
            <p className="text-white mb-0" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              {selectedExercise.routineName}
            </p>
          )}
          {selectedExercise?.routineDescription && (
            <p className="text-white mb-0" style={{ fontSize: '0.7rem', opacity: 0.6 }}>
              {selectedExercise.routineDescription}
            </p>
          )}
        </Container>

        {/* Botón de toggle de layout */}
        <Container className="text-end mb-1">
          <Button
            variant="light"
            size="sm"
            onClick={onToggleLayout}
            className="rounded-pill shadow-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
          >
            <LayoutList size={18} className="me-2" />
            {t('move.sideViewLabel')}
          </Button>
        </Container>

        <div
          className="turquoise-exercise-panel"
          style={{
            backgroundColor: '#8ef0e1',
            minHeight: '160px',
            padding: '1.5rem 0',
            borderRadius: '24px',
            marginTop: '0.5rem'
          }}
        >
        {/* Lista horizontal de ejercicios */}
        <Container fluid className="px-4">
          <div 
            className="exercise-scroll-container d-flex p-15"
            style={{ 
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}
          >
            {exercises.map((exercise, index) => {
              const isCompleted = completedExercises.includes(exercise.idVideo);
              const isSelected = selectedExercise?.idVideo === exercise.idVideo;

              return (
                <div className="flex-shrink-0 position-relative" style={{ margin: '0 0.5rem' }}>
                  {/* Checkmark para ejercicios completados - FUERA del card */}
                  {isCompleted && (
                    <div
                      className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        top: '-10px',
                        right: '-10px',
                        width: '32px',
                        height: '32px',
                        zIndex: 15,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        color: '#059669',
                        fontSize: '18px',
                        fontWeight: 'bold'
                      }}
                    >
                      ✓
                    </div>
                  )}

                  <div
                    key={exercise.idVideo}
                    onClick={() => onSelectExercise(exercise)}
                    className="exercise-card position-relative"
                    style={getCardStyles(isCompleted, exercise.idStoragePreview)}
                    onMouseEnter={(e) => {
                      if (exercise.idStoragePreview) {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 12px 25px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (exercise.idStoragePreview) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                  >
                  
                  {/* Imagen de fondo como elemento img alternativo */}
                  {exercise.idStoragePreview && exercise.idStoragePreview.trim() !== '' && (
                    <>
                      <img
                        src={exercise.idStoragePreview}
                        alt={exercise.name}
                        className="position-absolute"
                        style={{
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '16px',
                          zIndex: 1
                        }}
                        onError={(e) => {
                          console.log('Error loading image:', exercise.idStoragePreview);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', exercise.idStoragePreview);
                        }}
                      />

                      {/* Video overlay para mejor legibilidad del texto */}
                      <div
                        className="position-absolute"
                        style={{
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.1) 90%, transparent 100%)',
                          backdropFilter: 'blur(1px)',
                          borderRadius: '16px',
                          zIndex: 2,
                          overflow: 'hidden'
                        }}
                      />
                    </>
                  )}

                  <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 position-relative" style={{ zIndex: 3 }}>
                    {/* Nombre del video */}
                    <h5
                      className="text-white text-center mb-1"
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        lineHeight: '1.2',
                        opacity: 0.95,
                        maxWidth: '90%',
                        textShadow: exercise.idStoragePreview ? '2px 2px 4px rgba(0,0,0,0.7)' : 'none'
                      }}
                    >
                      {exercise.name || `Ejercicio ${index + 1}`}
                    </h5>

                    {/* Nombre de la rutina padre */}
                    <h6
                      className="text-white text-center mb-1"
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: '400',
                        lineHeight: '1.1',
                        opacity: 0.8,
                        maxWidth: '90%',
                        textShadow: exercise.idStoragePreview ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                      }}
                    >
                      {exercise.routineName || ''}
                    </h6>

                    {/* Duración del video */}
                    {exercise.duration && (
                      <p
                        className="text-white text-center mb-2"
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: '400',
                          lineHeight: '1.0',
                          opacity: 0.75,
                          textShadow: exercise.idStoragePreview ? '1px 1px 3px rgba(0,0,0,0.7)' : 'none'
                        }}
                      >
                        {Math.floor(exercise.duration / 60)}:{String(exercise.duration % 60).padStart(2, '0')}
                      </p>
                    )}
                  </div>
                  </div>
                </div>
              );
          })}
        </div>
      </Container>
    </div>
    </>
    );
  }

  // Renderizar en modo vertical (estilo YouTube)
  return (
    <div
      style={{
        backgroundColor: '#8ef0e1',
        borderRadius: '24px',
        padding: '1rem',
        height: 'calc(100vh - 150px)',
        maxHeight: '800px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header con título y botón de toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
        <h5 className="text-dark fw-bold mb-0" style={{ fontSize: '1rem' }}>
          {t('move.playlistLabel')}
        </h5>
        <Button
          variant="light"
          size="sm"
          onClick={onToggleLayout}
          className="rounded-pill shadow-sm"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
        >
          <LayoutGrid size={18} className="me-2" />
          {t('move.horizontalViewLabel')}
        </Button>
      </div>

      {/* Información del video actual */}
      <div className="mb-3 px-2">
        <h6 className="text-dark fw-semibold mb-1" style={{ fontSize: '0.9rem' }}>
          {selectedExercise?.name || t('move.exerciseLabel')}
        </h6>
        {selectedExercise?.routineName && (
          <p className="text-dark mb-0" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
            {selectedExercise.routineName}
          </p>
        )}
      </div>

      {/* Lista de videos con scroll vertical */}
      <div
        className="flex-grow-1"
        style={{
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {exercises.map((exercise, index) => {
          const isCompleted = completedExercises.includes(exercise.idVideo);
          const isSelected = selectedExercise?.idVideo === exercise.idVideo;

          return (
            <div
              key={exercise.idVideo}
              className="mb-2 position-relative"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectExercise(exercise)}
            >
              {/* Checkmark para completados */}
              {isCompleted && (
                <div
                  className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    top: '5px',
                    right: '5px',
                    width: '24px',
                    height: '24px',
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    color: '#059669',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ✓
                </div>
              )}

              {/* Card del video */}
              <div
                className="d-flex align-items-center p-2 rounded"
                style={{
                  backgroundColor: isSelected ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                  transition: 'all 0.2s ease',
                  border: isSelected ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                  }
                }}
              >
                {/* Thumbnail del video */}
                <div
                  className="flex-shrink-0 position-relative"
                  style={{
                    width: '120px',
                    height: '68px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: isCompleted ? '#67e8f9' : '#22bcc2'
                  }}
                >
                  {exercise.idStoragePreview && exercise.idStoragePreview.trim() !== '' ? (
                    <img
                      src={exercise.idStoragePreview}
                      alt={exercise.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null}
                </div>

                {/* Información del video */}
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-dark fw-semibold mb-1" style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>
                    {exercise.name || `Ejercicio ${index + 1}`}
                  </h6>
                  {exercise.routineName && (
                    <p className="text-dark mb-0" style={{ fontSize: '0.7rem', opacity: 0.7, lineHeight: '1.1' }}>
                      {exercise.routineName}
                    </p>
                  )}
                  {exercise.duration && (
                    <p className="text-dark mb-0" style={{ fontSize: '0.65rem', opacity: 0.6 }}>
                      {Math.floor(exercise.duration / 60)}:{String(exercise.duration % 60).padStart(2, '0')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExerciseList;